import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import * as blogApi from '@/api/blogApi';

interface SlugFieldProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  existingId?: string;
  label?: string;
  required?: boolean;
}

const SlugField: React.FC<SlugFieldProps> = ({
  title,
  value,
  onChange,
  existingId,
  label = 'Slug',
  required = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [titleChanged, setTitleChanged] = useState(false);
  
  // Create a simple client-side slug generator to use as fallback
  const createBasicSlug = useCallback((text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }, []);
  
  // Debounced slug generation to avoid too many API calls
  useEffect(() => {
    if (title && !value && titleChanged && !loading) {
      const timer = setTimeout(() => {
        generateSlug();
      }, 1000); // 1 second debounce
      
      return () => clearTimeout(timer);
    }
    // Reset titleChanged flag when value changes
    if (value) {
      setTitleChanged(false);
    }
  }, [title, value, titleChanged, loading]);
  
  // Update titleChanged when title changes
  useEffect(() => {
    if (title && !value) {
      setTitleChanged(true);
    }
  }, [title, value]);
  
  const generateSlug = async () => {
    if (!title) {
      toast.error('Please enter a title first');
      return;
    }
    
    try {
      setLoading(true);
      
      // Try client-side slug generation first to avoid API calls when possible
      const basicSlug = createBasicSlug(title);
      
      try {
        // Then try to get an optimized slug from the API
        const { slug: generatedSlug } = await blogApi.generateUniqueSlug(title, existingId);
        onChange(generatedSlug);
        setTitleChanged(false);
      } catch (apiError) {
        // Fallback to basic slug if API fails
        console.warn('API slug generation failed, using basic slug instead:', apiError);
        onChange(basicSlug);
        setTitleChanged(false);
      }
    } catch (error) {
      console.error('Error generating slug:', error);
      // Final fallback if everything else fails
      const fallbackSlug = createBasicSlug(title);
      onChange(fallbackSlug);
      setTitleChanged(false);
      toast.error('Error generating optimized slug. Using basic version instead.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="slug">{label} {required && '*'}</Label>
        <Button 
          type="button" 
          variant="secondary" 
          size="sm" 
          onClick={generateSlug}
          disabled={loading || !title}
          className="text-secondary-foreground"
        >
          {loading ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Generating...
            </>
          ) : (
            'Generate'
          )}
        </Button>
      </div>
      <Input
        id="slug"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="blog-post-url-slug"
        required={required}
        disabled={loading}
      />
      <p className="text-xs text-muted-foreground">
        This will be used in the blog post URL. Use only lowercase letters, numbers, and hyphens.
      </p>
    </div>
  );
};

export default SlugField; 